/**
 * Product Page Carousel JavaScript
 * Handles image carousel, variant selection, quantity controls, and add to cart
 */

class ProductCarousel {
  constructor() {
    this.productContainer = document.querySelector('.product-page');
    if (!this.productContainer) return;

    this.productId = this.productContainer.dataset.productId;
    this.productForm = document.querySelector('.product-form');
    this.variantInputs = document.querySelectorAll('.variant-input');
    this.quantityInput = document.querySelector('.quantity-input');
    this.quantityButtons = document.querySelectorAll('.quantity-btn');
    this.addToCartBtn = document.querySelector('.add-to-cart-btn, .buy-now-btn');
    this.detailToggles = document.querySelectorAll('[data-detail-toggle]');

    // Carousel elements
    this.mainImageContainer = document.querySelector('.main-image-container');
    this.thumbnails = document.querySelectorAll('.thumbnail-btn');
    this.prevBtn = document.querySelector('.prev-btn');
    this.nextBtn = document.querySelector('.next-btn');
    this.currentIndexEl = document.querySelector('.current-index');
    this.totalImagesEl = document.querySelector('.total-images');
    this.hiddenMedia = document.querySelectorAll('.hidden-media img, .hidden-media video');

    this.currentVariant = null;
    this.productData = null;
    this.currentImageIndex = 0;
    this.totalImages = this.thumbnails.length;

    this.init();
  }

  async init() {
    await this.fetchProductData();
    this.setupEventListeners();
    this.setupCarousel();
    this.setupCollapsibleDetails();
    this.updateVariantSelection();
    this.updateAddToCartButton();
    this.updateImageCounter();
  }

  async fetchProductData() {
    try {
      const handle = this.getProductHandle();
      if (!handle) {
        throw new Error('Missing product handle');
      }

      const response = await fetch(`/products/${handle}.js`);

      if (!response.ok) {
        throw new Error(`Product JSON fetch failed: ${response.status}`);
      }

      this.productData = await response.json();

      this.currentVariant =
        this.productData.variants.find((variant) => variant.available) ||
        this.productData.variants[0] ||
        null;

      if (!this.currentVariant) {
        throw new Error('No variants found for product');
      }
    } catch (error) {
      console.error('Error fetching product data:', error);

      // Fallback: use the variant already rendered in the form so the product
      // page can still submit even if Shopify preview-mode JSON is flaky.
      if (!this.currentVariant && this.productForm) {
        const variantIdInput = this.productForm.querySelector('input[name="id"]');
        if (variantIdInput?.value) {
          this.currentVariant = {
            id: Number(variantIdInput.value),
            available: !this.addToCartBtn?.disabled
          };
        }
      }
    }
  }

  getProductHandle() {
    return this.productContainer?.dataset.productHandle || null;
  }

  setupEventListeners() {
    // Variant selection
    this.variantInputs.forEach((input) => {
      input.addEventListener('change', this.handleVariantChange.bind(this));
    });

    // Quantity controls
    this.quantityButtons.forEach((btn) => {
      btn.addEventListener('click', this.handleQuantityChange.bind(this));
    });

    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', this.validateQuantity.bind(this));
      this.quantityInput.addEventListener('input', this.validateQuantity.bind(this));
    }

    // Add to cart
    if (this.productForm) {
      this.productForm.addEventListener('submit', this.handleAddToCart.bind(this));
    }

    // Carousel navigation
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => this.goToImage(index));
      thumbnail.addEventListener('keydown', (e) => this.handleThumbnailKeydown(e, index));
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', this.previousImage.bind(this));
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', this.nextImage.bind(this));
    }

    // Keyboard navigation for carousel
    document.addEventListener('keydown', this.handleKeyboardNavigation.bind(this));

    // Touch/swipe support for mobile
    this.setupTouchNavigation();
  }

  setupCarousel() {
    if (this.totalImages === 0) return;

    this.updateNavigationButtons();
    this.updateThumbnailsActive();
  }

  setupCollapsibleDetails() {
    this.detailToggles.forEach((toggle) => {
      toggle.addEventListener('click', this.handleDetailToggle.bind(this));
    });
  }

  setupTouchNavigation() {
    if (!this.mainImageContainer) return;

    let startX = 0;
    let startY = 0;
    let isDragging = false;

    this.mainImageContainer.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = true;
      },
      { passive: true }
    );

    this.mainImageContainer.addEventListener(
      'touchmove',
      (e) => {
        if (!isDragging) return;

        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const diffX = startX - currentX;
        const diffY = startY - currentY;

        // Prevent vertical scrolling if horizontal swipe is more significant
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 30) {
          e.preventDefault();
        }
      },
      { passive: false }
    );

    this.mainImageContainer.addEventListener(
      'touchend',
      (e) => {
        if (!isDragging) return;

        const endX = e.changedTouches[0].clientX;
        const diffX = startX - endX;

        if (Math.abs(diffX) > 50) {
          if (diffX > 0) {
            this.nextImage();
          } else {
            this.previousImage();
          }
        }

        isDragging = false;
      },
      { passive: true }
    );
  }

  goToImage(index) {
    if (index < 0 || index >= this.totalImages) return;

    this.currentImageIndex = index;

    // Get the target media element
    const targetThumbnail = this.thumbnails[index];
    const mediaId = targetThumbnail.dataset.mediaId;

    this.switchToMedia(mediaId);
    this.updateImageCounter();
    this.updateNavigationButtons();
    this.updateThumbnailsActive();
  }

  nextImage() {
    const nextIndex = this.currentImageIndex + 1;
    if (nextIndex < this.totalImages) {
      this.goToImage(nextIndex);
    }
  }

  previousImage() {
    const prevIndex = this.currentImageIndex - 1;
    if (prevIndex >= 0) {
      this.goToImage(prevIndex);
    }
  }

  switchToMedia(mediaId) {
    const hiddenMediaElement = document.querySelector(`.hidden-media [data-media-id="${mediaId}"]`);
    const mainContainer = this.mainImageContainer;

    if (!hiddenMediaElement || !mainContainer) return;

    const currentMedia = mainContainer.querySelector('.main-image, .main-video');
    if (currentMedia) {
      currentMedia.remove();
    }

    const newMedia = hiddenMediaElement.cloneNode(true);
    newMedia.classList.remove('hidden-image', 'hidden-video');
    newMedia.classList.add(hiddenMediaElement.tagName.toLowerCase() === 'img' ? 'main-image' : 'main-video');
    newMedia.classList.add('active');

    const firstNav = mainContainer.querySelector('.carousel-nav');
    if (firstNav) {
      mainContainer.insertBefore(newMedia, firstNav);
    } else {
      mainContainer.appendChild(newMedia);
    }
  }

  updateImageCounter() {
    if (this.currentIndexEl) {
      this.currentIndexEl.textContent = this.currentImageIndex + 1;
    }
    if (this.totalImagesEl) {
      this.totalImagesEl.textContent = this.totalImages;
    }
  }

  updateNavigationButtons() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentImageIndex === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentImageIndex === this.totalImages - 1;
    }
  }

  updateThumbnailsActive() {
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.classList.toggle('active', index === this.currentImageIndex);
    });
  }

  handleThumbnailKeydown(event, index) {
    let targetIndex = index;

    switch (event.key) {
      case 'ArrowLeft':
        targetIndex = index > 0 ? index - 1 : this.totalImages - 1;
        break;
      case 'ArrowRight':
        targetIndex = index < this.totalImages - 1 ? index + 1 : 0;
        break;
      case 'Home':
        targetIndex = 0;
        break;
      case 'End':
        targetIndex = this.totalImages - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.thumbnails[targetIndex].focus();
    this.goToImage(targetIndex);
  }

  handleKeyboardNavigation(event) {
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.previousImage();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.nextImage();
        break;
    }
  }

  handleVariantChange() {
    const selectedOptions = this.getSelectedOptions();
    const matchingVariant = this.findVariantByOptions(selectedOptions);

    if (matchingVariant) {
      this.currentVariant = matchingVariant;
      this.updateVariantSelection();
      this.updatePrice();
      this.updateAddToCartButton();
      this.updateVariantImage();
      this.updateURL();
    }

    this.updateVariantAvailability();
  }

  getSelectedOptions() {
    const options = {};
    this.variantInputs.forEach((input) => {
      if (input.checked) {
        const optionName = input.name.replace('options[', '').replace(']', '');
        options[optionName] = input.value;
      }
    });
    return options;
  }

  findVariantByOptions(selectedOptions) {
    if (!this.productData) return null;

    return this.productData.variants.find((variant) => {
      return variant.options.every((option, index) => {
        const optionName = this.productData.options[index];
        return selectedOptions[optionName] === option;
      });
    });
  }

  updateVariantSelection() {
    if (!this.currentVariant || !this.productForm) return;

    const variantIdInput = this.productForm.querySelector('input[name="id"]');
    if (variantIdInput) {
      variantIdInput.value = this.currentVariant.id;
    }
  }

  updatePrice() {
    if (!this.currentVariant) return;

    const priceElement = document.querySelector('[data-price]');
    const comparePriceElement = document.querySelector('[data-compare-price]');

    if (priceElement) {
      priceElement.textContent = this.formatMoney(this.currentVariant.price);
    }

    if (comparePriceElement && this.currentVariant.compare_at_price > this.currentVariant.price) {
      comparePriceElement.textContent = this.formatMoney(this.currentVariant.compare_at_price);
      comparePriceElement.style.display = 'inline';
      priceElement?.classList.add('price-on-sale');
    } else if (comparePriceElement) {
      comparePriceElement.style.display = 'none';
      priceElement?.classList.remove('price-on-sale');
    }

    this.updateInventoryNotice();
  }

  updateInventoryNotice() {
    if (!this.currentVariant) return;

    let inventoryNotice = document.querySelector('.inventory-notice');

    if (
      this.currentVariant.inventory_management === 'shopify' &&
      this.currentVariant.inventory_quantity <= 10 &&
      this.currentVariant.inventory_quantity > 0
    ) {
      if (!inventoryNotice) {
        inventoryNotice = document.createElement('div');
        inventoryNotice.className = 'inventory-notice';
        const priceWrapper = document.querySelector('.product-price-wrapper');
        if (priceWrapper) {
          priceWrapper.appendChild(inventoryNotice);
        }
      }

      inventoryNotice.innerHTML = `<span class="inventory-count">Only ${this.currentVariant.inventory_quantity} left in stock</span>`;
    } else if (inventoryNotice) {
      inventoryNotice.remove();
    }
  }

  updateAddToCartButton() {
    if (!this.addToCartBtn || !this.currentVariant) return;

    const btnText = this.addToCartBtn.querySelector('.btn-text');
    const isBuyNow = this.addToCartBtn.dataset.buyNow === 'true';

    if (this.currentVariant.available) {
      this.addToCartBtn.disabled = false;
      if (btnText) {
        btnText.textContent = isBuyNow ? 'Buy Now' : 'Add To Cart';
      }
    } else {
      this.addToCartBtn.disabled = true;
      if (btnText) {
        btnText.textContent = 'Sold Out';
      }
    }
  }

  updateVariantImage() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;

    const targetThumbnail = document.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    if (targetThumbnail) {
      const targetIndex = parseInt(targetThumbnail.dataset.index, 10);
      this.goToImage(targetIndex);
    }
  }

  updateVariantAvailability() {
    this.variantInputs.forEach((input) => {
      const tempOptions = this.getSelectedOptions();
      const optionName = input.name.replace('options[', '').replace(']', '');
      tempOptions[optionName] = input.value;

      const testVariant = this.findVariantByOptions(tempOptions);
      const label = input.nextElementSibling;

      if (testVariant && !testVariant.available) {
        input.disabled = true;
        if (label) label.classList.add('unavailable');
      } else {
        input.disabled = false;
        if (label) label.classList.remove('unavailable');
      }
    });
  }

  updateURL() {
    if (!this.currentVariant) return;

    const url = new URL(window.location);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url);
  }

  handleQuantityChange(event) {
    const isIncrease = event.target.classList.contains('quantity-plus');
    const isDecrease = event.target.classList.contains('quantity-minus');

    if (!this.quantityInput || (!isIncrease && !isDecrease)) return;

    let currentValue = parseInt(this.quantityInput.value, 10) || 1;

    if (isIncrease) {
      currentValue += 1;
    } else if (isDecrease && currentValue > 1) {
      currentValue -= 1;
    }

    this.quantityInput.value = currentValue;
    this.validateQuantity();
  }

  validateQuantity() {
    if (!this.quantityInput) return;

    let value = parseInt(this.quantityInput.value, 10) || 1;
    const min = parseInt(this.quantityInput.min, 10) || 1;
    const max = parseInt(this.quantityInput.max, 10) || 999;

    value = Math.max(min, Math.min(max, value));
    this.quantityInput.value = value;

    const minusBtn = document.querySelector('.quantity-minus');
    const plusBtn = document.querySelector('.quantity-plus');

    if (minusBtn) minusBtn.disabled = value <= min;
    if (plusBtn) plusBtn.disabled = value >= max;
  }

  async handleAddToCart(event) {
    event.preventDefault();

    if (!this.productForm || !this.addToCartBtn) return;
    if (!this.currentVariant || !this.currentVariant.available) return;

    const isBuyNow = this.addToCartBtn.dataset.buyNow === 'true';

    this.updateVariantSelection();
    this.setLoadingState(true);

    try {
      const formData = new FormData(this.productForm);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add to cart: ${response.status} ${errorText}`);
      }

      const result = await response.json();

      if (isBuyNow) {
        window.location.href = '/cart';
      } else {
        this.handleAddToCartSuccess(result);
        this.setLoadingState(false);
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      this.handleAddToCartError(error);
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    if (!this.addToCartBtn) return;

    if (isLoading) {
      this.addToCartBtn.classList.add('loading');
      this.addToCartBtn.disabled = true;
    } else {
      this.addToCartBtn.classList.remove('loading');
      this.addToCartBtn.disabled = !this.currentVariant?.available;
    }
  }

  handleAddToCartSuccess(result) {
    document.dispatchEvent(
      new CustomEvent('cart:added', {
        detail: { variant: this.currentVariant, quantity: result.quantity }
      })
    );

    this.showNotification('Added to cart!', 'success');
    this.updateCartCount();
  }

  handleAddToCartError() {
    this.showNotification('Error adding to cart. Please try again.', 'error');
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartCountElements = document.querySelectorAll('.cart-count');
      cartCountElements.forEach((element) => {
        element.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      background: ${type === 'success' ? '#22c55e' : '#ef4444'};
      color: white;
      border-radius: 6px;
      z-index: 1000;
      animation: slideIn 0.3s ease;
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  handleDetailToggle(event) {
    const toggle = event.currentTarget;
    const detailItem = toggle.closest('.detail-item');
    const content = detailItem.querySelector('[data-detail-content]');
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      toggle.setAttribute('aria-expanded', 'false');
      content.style.height = '0px';
    } else {
      toggle.setAttribute('aria-expanded', 'true');
      content.style.height = content.scrollHeight + 'px';
    }
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }
}

class RxLensFlowEnhancer {
  constructor() {
    if (window.__mentraRxInlineFocusActive) return;

    this.productPage = document.querySelector('.product-page');
    this.isRxFlow =
      document.body.classList.contains('rx-focus-product') &&
      this.productPage?.dataset.productHandle === 'mentra-live-single-vision-rx';

    if (!this.isRxFlow) return;

    this.hostObserver = null;
    this.modalObserver = null;
    this.bodyObserver = null;

    this.init();
  }

  init() {
    this.enhanceHostPage();
    this.observeHostPage();
    this.observeModalLifecycle();
    this.syncModalState();
  }

  enhanceHostPage() {
    const header = document.querySelector('.product-info-section--rx .product-header');
    if (header) {
      header.classList.add('product-header--rx');

      let eyebrow = header.querySelector('.rx-focus-context__eyebrow');
      if (!eyebrow) {
        eyebrow = document.createElement('p');
        eyebrow.className = 'rx-focus-context__eyebrow';
        header.prepend(eyebrow);
      }
      eyebrow.textContent = 'Mentra Live extension';

      const title = header.querySelector('.product-title');
      if (title) {
        title.classList.add('rx-focus-page-title');
        title.textContent = 'Prescription lens setup';
      }

      let bodyCopy = header.querySelector('.rx-focus-context__body');
      if (!bodyCopy) {
        bodyCopy = document.createElement('p');
        bodyCopy.className = 'rx-focus-context__body';
        header.appendChild(bodyCopy);
      }
      bodyCopy.textContent =
        'Choose your prescription type and continue. This is the lens step for your Mentra Live frame, not a separate product page.';
    }

    [
      '.product-media-section',
      '.product-quantity',
      '.product-details',
      '.shipping-info',
      '.rx-focus-intro',
      '.product-price-wrapper'
    ].forEach((selector) => {
      document.querySelectorAll(selector).forEach((node) => {
        node.setAttribute('hidden', 'hidden');
        node.classList.add('rx-runtime-hidden');
      });
    });

    const form = document.querySelector('.product-form');
    form?.classList.add('product-form--rx-runtime');

    const widget = document.querySelector('.lensadvisor-widget');
    if (widget) {
      const eyebrow = widget.querySelector('.lensadvisor-status__eyebrow');
      const status = widget.querySelector('.lensadvisor-status');
      const launcher = widget.querySelector('#la-select-lenses-btn');

      if (eyebrow) eyebrow.textContent = 'Prescription extension';
      if (status) {
        status.textContent =
          'Start with your prescription type. On the next screen you can upload your prescription, enter it manually, or email it later.';
      }
      if (launcher) launcher.textContent = 'Start prescription';
    }
  }

  observeHostPage() {
    if (this.hostObserver || !document.body) return;

    this.hostObserver = new MutationObserver(() => this.enhanceHostPage());
    this.hostObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  observeModalLifecycle() {
    if (this.bodyObserver || !document.body) return;

    this.bodyObserver = new MutationObserver(() => this.syncModalState());
    this.bodyObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  syncModalState() {
    const modal = document.getElementById('lensadvizor-prescription-modal');
    const isOpen = Boolean(modal && modal.classList.contains('la-show'));

    document.documentElement.classList.toggle('rx-lensadvisor-open', isOpen);
    document.body.classList.toggle('rx-lensadvisor-open', isOpen);

    if (!modal) return;

    this.enhanceModal(modal);

    if (!this.modalObserver) {
      this.modalObserver = new MutationObserver(() => this.enhanceModal(modal));
      this.modalObserver.observe(modal, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  enhanceModal(modal) {
    modal.classList.add('mentra-rx-modal');

    [
      '.la-prescription-modal-image',
      '#la-prescription-modal-title',
      '#la-product-featured-image',
      '#la-image-loader-internal'
    ].forEach((selector) => {
      modal.querySelectorAll(selector).forEach((node) => {
        node.setAttribute('hidden', 'hidden');
        node.style.display = 'none';
      });
    });

    const wrapper = modal.querySelector('.la-prescription-modal-wrapper');
    if (wrapper) {
      wrapper.style.gridTemplateColumns = '1fr';
    }

    const heading = modal.querySelector('#la_prescriptionSelect_title, .la-wrapper-heading h3');
    const headingContainer = modal.querySelector('.la-wrapper-heading');
    if (heading && headingContainer) {
      let kicker = headingContainer.querySelector('.mentra-rx-modal-kicker');
      if (!kicker) {
        kicker = document.createElement('p');
        kicker.className = 'mentra-rx-modal-kicker';
        headingContainer.insertBefore(kicker, heading);
      }

      let bodyCopy = headingContainer.querySelector('.mentra-rx-modal-copy');
      if (!bodyCopy) {
        bodyCopy = document.createElement('p');
        bodyCopy.className = 'mentra-rx-modal-copy';
        heading.insertAdjacentElement('afterend', bodyCopy);
      }

      kicker.textContent = 'Mentra Live extension';
      bodyCopy.textContent = this.getModalHelperCopy(heading.textContent || '');
    }

    const footer = modal.querySelector('.la-prescription-modal-footer');
    const footerButtonGroup = modal.querySelector('.la-subtotal-footer-button');
    const footerButton = modal.querySelector('.la-prescription-form-btn');
    if (footer) {
      const shouldHideFooter =
        !footerButtonGroup ||
        footerButtonGroup.style.display === 'none' ||
        footerButtonGroup.hidden ||
        (footerButton && footerButton.disabled && modal.querySelector('.la-label-wrapper'));

      footer.dataset.mentraHidden = shouldHideFooter ? 'true' : 'false';
    }
  }

  getModalHelperCopy(stepTitle) {
    const title = stepTitle.trim().toLowerCase();

    if (title.includes('prescription type')) {
      return 'Pick the prescription path that fits this order. The next step is how you want to share your prescription.';
    }

    if (title.includes('add your prescription')) {
      return 'Use the method that is fastest for you. Uploading is quickest, but manual entry and email later stay available.';
    }

    if (title.includes('select lenses') || title.includes('lens')) {
      return 'Choose the lens build that matches how you plan to wear Mentra Live. Keep the selection focused on function, not frame changes.';
    }

    if (title.includes('review')) {
      return 'Confirm the prescription details and lens setup before continuing to the cart.';
    }

    return 'Complete the prescription setup for your Mentra Live frame and continue when the step is ready.';
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new ProductCarousel();
  new RxLensFlowEnhancer();
});

// Add CSS animation styles
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  @keyframes slideOut {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(100%); opacity: 0; }
  }
`;
document.head.appendChild(style);
